/**
 * MatchmakingRouter Component
 *
 * Routes users to the appropriate matchmaking component based on their role.
 * - CARE_SEEKER → CareSeekerMatchmaking (separate, standalone component)
 * - FINANCIAL_ADVISOR → ProDashboardMatchmaking (shared professional component)
 * - SERVICE_PROVIDER → ProDashboardMatchmaking (shared professional component)
 *
 * This router ensures complete separation between Care Seeker and professional matchmaking flows,
 * preventing any professional validation logic from interfering with Care Seeker access.
 *
 * Navigator is a paid AI service and is not available to unauthenticated users.
 * /matchmaking is mounted inside the ProtectedRoute block in routes/index.tsx;
 * guest visitors see a locked tab in PreLoginShell that opens the signup modal.
 */

import { useBackNavigate } from '@/shared/hooks/useBackNavigate';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { Spinner } from '@/shared/components/Loading/Spinner';
import { PermissionGate, useWorkspace } from '@/features/workspace-sub-delegation';
import { CareSeekerMatchmaking } from './CareSeekerMatchmaking';
import ProDashboardMatchmaking from './ProDashboardMatchmaking';

export const MatchmakingRouter = () => {
  const { user, isLoading } = useAuth();
  // For a SUB_DELEGATE the matchmaking experience follows the workspace
  // owner's role (effectiveRole), and is gated by WORKSPACE_OPERATIONS.
  const { effectiveRole, isDelegate, isLoading: isWorkspaceLoading } = useWorkspace();
  const goBack = useBackNavigate();

  // Show loading spinner while checking authentication or resolving the
  // delegate's active workspace. /matchmaking lives inside ProtectedRoute, so an
  // absent user here is transient (still resolving) — no explicit redirect.
  if (isLoading || !user || (isDelegate && isWorkspaceLoading)) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center">
          <Spinner className="mx-auto mb-4" />
          <p className="text-muted-foreground">Loading matchmaking...</p>
        </div>
      </div>
    );
  }

  // Route by the effective role (owner's role for delegates, own role
  // otherwise). CRITICAL: these are SEPARATE components, not conditional renders.
  const renderForRole = () => {
    switch (effectiveRole) {
      case 'CARE_SEEKER':
        return <CareSeekerMatchmaking />;

      case 'FINANCIAL_ADVISOR':
      case 'SERVICE_PROVIDER':
        return <ProDashboardMatchmaking />;

      default:
        // Other roles don't have access to matchmaking
        return (
          <div className="flex h-screen items-center justify-center bg-background">
            <div className="max-w-md text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                Access Restricted
              </h1>
              <p className="text-gray-600">
                bQuest Navigator is only available for Care Seekers, Service Providers, and Financial Advisors.
              </p>
              <button
                onClick={() => goBack('/')}
                className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                Go Back
              </button>
            </div>
          </div>
        );
    }
  };

  // WORKSPACE_OPERATIONS gates Navigator for delegates; transparent
  // (always-allowed) for owners acting in their own workspace.
  return (
    <PermissionGate permission="WORKSPACE_OPERATIONS" feature="Navigator" variant="page">
      {renderForRole()}
    </PermissionGate>
  );
};

export default MatchmakingRouter;
